import AssignmentTurnedIn from "@mui/icons-material/AssignmentTurnedIn";
import Business from "@mui/icons-material/Business";
import Contacts from "@mui/icons-material/Contacts";
import Delete from "@mui/icons-material/Delete";
import Description from "@mui/icons-material/Description";
import Home from "@mui/icons-material/Home";
import Info from "@mui/icons-material/Info";
import Map from "@mui/icons-material/Map";
import NotificationsActive from "@mui/icons-material/NotificationsActive";
import Send from "@mui/icons-material/Send";
import UploadFile from "@mui/icons-material/UploadFile";
import { SubmissionStepType } from "generated-graphql/graphql";
import React from "react";
import { ContactsStep } from "routes/Customer/Waste/Submissions/Stepper/steps/ContactsStep";
import { FacilityProfileStep } from "routes/Customer/Waste/Submissions/Stepper/steps/FacilityProfileStep";
import { FilingNotificationsStep } from "routes/Customer/Waste/Submissions/Stepper/steps/FilingNotificationsStep";
import { PrepareDocumentStep } from "routes/Customer/Waste/Submissions/Stepper/steps/PrepareDocumentStep";
import { ReviewStep } from "routes/Customer/Waste/Submissions/Stepper/steps/ReviewStep";
import { StateFieldsStep } from "routes/Customer/Waste/Submissions/Stepper/steps/StateFieldsStep";
import { SubmitAndFileStep } from "routes/Customer/Waste/Submissions/Stepper/steps/SubmitAndFileStep";
import { UnderstandStep } from "routes/Customer/Waste/Submissions/Stepper/steps/UnderstandStep";
import { WasteActivityStep } from "routes/Customer/Waste/Submissions/Stepper/steps/WasteActivityStep";

export type SubmissionStepPresentation = {
  label: string;
  sourceDescription: string;
  /** Route segment the FE routes off; "" for the overview step. */
  routeSlug: string;
  icon: React.ReactNode;
  /**
   * Editor mounted by `StepRoute` for this step. Omitted for OVERVIEW, which is
   * the index route and renders `StepperOverview` directly (never via `StepRoute`).
   */
  Editor?: React.ComponentType<{ submissionId: string; facilityId: string }>;
};

export const WASTE_SUBMISSION_STEP_PRESENTATION: Record<
  SubmissionStepType,
  SubmissionStepPresentation
> = {
  [SubmissionStepType.Overview]: {
    label: "Overview",
    sourceDescription: "Review and validate the 8700-12 submission.",
    routeSlug: "",
    icon: <Home />,
  },
  [SubmissionStepType.SubmissionDetails]: {
    // Details is rendered inline at the top of the Overview (see `DetailsCard`),
    // not as a step in the nav — so it has no route/editor. The metadata entry
    // stays because the type requires every `SubmissionStepType`.
    label: "Details",
    sourceDescription: "Submission title, due date, and Encamp assignee.",
    routeSlug: "details",
    icon: <Description />,
  },
  [SubmissionStepType.FacilityProfile]: {
    label: "Facility Profile",
    sourceDescription: "Sourced from the facility's RCRA profile.",
    routeSlug: "facility-profile",
    icon: <Business />,
    Editor: FacilityProfileStep,
  },
  [SubmissionStepType.RcraWasteActivity]: {
    label: "Waste Activity",
    sourceDescription:
      "Federal & state generator status, waste streams, and reportable waste codes, all computed from manifest and vendor data.",
    routeSlug: "waste-activity",
    icon: <Delete />,
    Editor: WasteActivityStep,
  },
  [SubmissionStepType.Contacts]: {
    label: "Contacts",
    sourceDescription: "RCRA site contact, owner, operator, and additional contacts.",
    routeSlug: "contacts",
    icon: <Contacts />,
    Editor: ContactsStep,
  },
  [SubmissionStepType.StateFields]: {
    label: "State Info",
    sourceDescription: "State-specific RCRA fields for this facility's jurisdiction.",
    routeSlug: "state-info",
    icon: <Map />,
    Editor: StateFieldsStep,
  },
  [SubmissionStepType.FilingNotifications]: {
    label: "Filing Notifications",
    sourceDescription:
      "Optional 8700-12 notifications: episodic generation, LQG site closure, and hazardous secondary material.",
    routeSlug: "filing-notifications",
    icon: <NotificationsActive />,
    Editor: FilingNotificationsStep,
  },
  [SubmissionStepType.Review]: {
    label: "Review",
    sourceDescription:
      "Reviewers sign off before submission; the final approval submits for processing.",
    routeSlug: "review",
    icon: <AssignmentTurnedIn />,
    Editor: ReviewStep,
  },
  [SubmissionStepType.Understand]: {
    label: "Understand",
    sourceDescription: "What this submission is, why it is required, and its scope.",
    routeSlug: "understand",
    icon: <Info />,
    Editor: UnderstandStep,
  },
  [SubmissionStepType.PrepareDocument]: {
    label: "Prepare Document",
    sourceDescription: "Attach the filled-out submission document and any supporting attachments.",
    routeSlug: "prepare-document",
    icon: <UploadFile />,
    Editor: PrepareDocumentStep,
  },
  [SubmissionStepType.SubmitAndFile]: {
    label: "Submit & File",
    sourceDescription: "Filing details and the signed submission.",
    routeSlug: "submit-and-file",
    icon: <Send />,
    Editor: SubmitAndFileStep,
  },
};
